<template>
  <AppLayout>
    <div class="transaction fleet-module-page">
      <section class="container">

        <!-- Stats -->
        <div class="fleet-stat-grid">
          <div class="fleet-stat-card">
            <span>Total Assignments</span>
            <strong>{{ assignments.length }}</strong>
          </div>
          <div class="fleet-stat-card">
            <span>Vehicles Assigned</span>
            <strong>{{ uniqueVehicles }}</strong>
          </div>
          <div class="fleet-stat-card">
            <span>Drivers Assigned</span>
            <strong>{{ uniqueDrivers }}</strong>
          </div>
        </div>

        <!-- Filters -->
        <div class="filter-search">
          <div class="filter-search-content">
            <div class="row">
              <div class="col-md-5">
                <div class="form-group">
                  <label>Filter by Vehicle</label>
                  <select v-model="filters.vehicleId" class="form-control" @change="onFilterChange">
                    <option value="">All Vehicles</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ vehicleLabel(v) }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-5">
                <div class="form-group">
                  <label>Filter by Driver</label>
                  <select v-model="filters.driverId" class="form-control" @change="onFilterChange">
                    <option value="">All Drivers</option>
                    <option v-for="d in drivers" :key="d.id" :value="d.id">{{ driverLabel(d) }}</option>
                  </select>
                </div>
              </div>
              <div class="col-md-2 d-flex align-items-end">
                <button class="btn btn-sm btn-style-2 w-100" @click="loadData">Refresh</button>
              </div>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <div class="table-toolbar-actions">
            <button class="btn btn-style-2 btn-sm" @click="openAssign">Assign Driver</button>
          </div>
        </div>

        <!-- DataTable -->
        <div class="data-table-wrapper">
          <div class="table-wrap">
            <table ref="fleetTable" class="fleet-table table table-hover">
              <thead>
                <tr>
                  <th>No.</th>
                  <th>Vehicle</th>
                  <th>Driver</th>
                  <th>Driver Phone</th>
                  <th>Assigned Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(a, index) in filteredAssignments" :key="`${a?.vehicleId ?? index}-${a?.driverId ?? index}`">
                  <td>{{ index + 1 }}</td>
                  <td>{{ vehicleLabel(a.vehicle) }}</td>
                  <td>{{ driverLabel(a.driver) }}</td>
                  <td>{{ a.driver?.phone || '-' }}</td>
                  <td>{{ fmtDate(a.createdAt) }}</td>
                  <td>
                    <div class="table-actions">
                      <button class="mini-btn danger" @click="doUnassign(a)">Unassign</button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Assign Modal -->
        <div v-if="showAssign" class="svc-modal-overlay" @click.self="showAssign = false">
          <div class="svc-modal-box" style="max-width:440px;">
            <div class="svc-modal-header">
              <h6 class="fw-bold mb-0">Assign Driver to Vehicle</h6>
              <button class="btn-close-plain" @click="showAssign = false">&times;</button>
            </div>
            <div class="svc-modal-body">
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Vehicle <span class="text-danger">*</span></label>
                  <select v-model.number="assignForm.vehicleId" class="form-control form-control-sm">
                    <option :value="null">Select vehicle</option>
                    <option v-for="v in vehicles" :key="v.id" :value="v.id">{{ vehicleLabel(v) }}</option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label" style="font-size:11px;">Driver <span class="text-danger">*</span></label>
                  <select v-model.number="assignForm.driverId" class="form-control form-control-sm">
                    <option :value="null">Select driver</option>
                    <option v-for="d in drivers" :key="d.id" :value="d.id">{{ driverLabel(d) }}</option>
                  </select>
                </div>
              </div>
              <p v-if="assignError" class="text-danger small mt-2">{{ assignError }}</p>
            </div>
            <div class="svc-modal-footer">
              <button class="btn btn-sm btn-style-2" @click="showAssign = false">Cancel</button>
              <button class="btn btn-sm btn-primary" @click="saveAssign" :disabled="saving">
                <span v-if="saving"><i class="fas fa-spinner fa-spin me-1"></i>Saving...</span>
                <span v-else>Assign</span>
              </button>
            </div>
          </div>
        </div>

      </section>
    </div>
  </AppLayout>
</template>

<script>
import AppLayout from '@/components/AppLayout.vue';
import fleetApi from '@/api/fleet';
import Swal from '@/utils/swal';
import 'datatables.net-vue3';
import 'datatables.net-bs5';
import 'datatables.net-buttons-bs5';
import 'datatables.net-responsive-bs5';
import $ from 'jquery';
import 'jszip';
import 'datatables.net-buttons/js/buttons.html5.js';
import 'datatables.net-buttons/js/buttons.colVis.js';
import 'datatables.net-buttons/js/buttons.print.js';

export default {
  name: 'FleetDriverAssignments',
  components: { AppLayout },
  data() {
    return {
      assignments: [],
      vehicles: [],
      drivers: [],
      dataTable: null,
      saving: false,
      filters: {
        vehicleId: '',
        driverId: '',
      },
      showAssign: false,
      assignForm: { vehicleId: null, driverId: null },
      assignError: '',
    };
  },
  computed: {
    uniqueVehicles() {
      return new Set(this.assignments.map(a => a.vehicleId)).size;
    },
    uniqueDrivers() {
      return new Set(this.assignments.map(a => a.driverId)).size;
    },
    filteredAssignments() {
      return this.assignments.filter(a => {
        if (!a || a.vehicleId == null || a.driverId == null) return false;
        const matchVehicle = !this.filters.vehicleId || a.vehicleId === this.filters.vehicleId;
        const matchDriver = !this.filters.driverId || a.driverId === this.filters.driverId;
        return matchVehicle && matchDriver;
      });
    },
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.initializeDataTable();
      },
    },
  },
  mounted() {
    this.loadData();
  },
  beforeUnmount() {
    this.destroyDataTable();
  },
  methods: {
    destroyDataTable() {
      if (this.dataTable && typeof this.dataTable.destroy === 'function') {
        this.dataTable.destroy();
        this.dataTable = null;
      }
    },
    initializeDataTable() {
      this.$nextTick(() => {
        const tableElement = this.$refs.fleetTable;
        if (!tableElement) return;
        this.destroyDataTable();
        this.dataTable = $(tableElement).DataTable({
          responsive: true,
          destroy: true,
          pageLength: 10,
          lengthMenu: [[10, 25, 50, 100, -1], [10, 25, 50, 100, 'All']],
          language: {
            emptyTable: 'No assignments found',
            paginate: {
              previous: '<i class="fa fa-chevron-left"></i>',
              next: '<i class="fa fa-chevron-right"></i>',
            },
          },
          dom: 'Bfrtip',
          buttons: [
            'colvis',
            'pageLength',
            {
              extend: 'csv',
              text: 'Export CSV',
              className: 'btn btn-sm btn-outline-primary',
              exportOptions: { columns: ':visible' },
            },
          ],
        });
      });
    },
    async loadData() {
      try {
        const [vehiclesRes, driversRes] = await Promise.all([
          fleetApi.listVehicles({ page: 1, limit: 200 }),
          fleetApi.listDrivers({ page: 1, limit: 200 }),
        ]);
        this.vehicles = fleetApi.unwrapList(vehiclesRes);
        this.drivers = fleetApi.unwrapList(driversRes);
        await this.loadAssignments();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load data');
      }
    },
    onFilterChange() {
      this.loadAssignments();
    },
    async loadAssignments() {
      try {
        if (this.filters.vehicleId) {
          const res = await fleetApi.getVehicleDrivers(this.filters.vehicleId);
          const items = Array.isArray(res) ? res : (res?.data ?? []);
          const vehicle = this.vehicles.find(v => v.id === this.filters.vehicleId);
          this.assignments = items.map(d => ({
            vehicleId: this.filters.vehicleId,
            driverId: d.driverId ?? d.id,
            vehicle,
            driver: d.driver ?? d,
            createdAt: d.createdAt,
          }));
        } else if (this.filters.driverId) {
          const res = await fleetApi.getDriverVehicles(this.filters.driverId);
          const items = Array.isArray(res) ? res : (res?.data ?? []);
          const driver = this.drivers.find(d => d.id === this.filters.driverId);
          this.assignments = items.map(v => ({
            vehicleId: v.vehicleId ?? v.id,
            driverId: this.filters.driverId,
            vehicle: v.vehicle ?? v,
            driver,
            createdAt: v.createdAt,
          }));
        } else {
          const all = [];
          await Promise.all(this.vehicles.map(async (vehicle) => {
            try {
              const aRes = await fleetApi.getVehicleDrivers(vehicle.id);
              const items = Array.isArray(aRes) ? aRes : (aRes?.data ?? []);
              for (const item of items) {
                const driverId = item.driverId ?? item.driver?.id ?? item.id;
                if (!driverId) continue;
                all.push({
                  vehicleId: vehicle.id,
                  driverId,
                  vehicle,
                  driver: item.driver ?? item,
                  createdAt: item.createdAt,
                });
              }
            } catch (_) { /* skip */ }
          }));
          this.assignments = all;
        }
        this.initializeDataTable();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to load assignments');
      }
    },
    vehicleLabel(v) {
      if (!v) return '-';
      const d = [v.make, v.model].filter(Boolean).join(' ');
      return d ? `${v.plateNumber} — ${d}` : (v.plateNumber || `Vehicle #${v.id}`);
    },
    driverLabel(d) {
      if (!d) return '-';
      const name = d.user
        ? `${d.user.firstName ?? ''} ${d.user.lastName ?? ''}`.trim()
        : `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim();
      return name || `Driver #${d.id}`;
    },
    fmtDate(v) {
      if (!v) return '-';
      return new Date(v).toLocaleDateString();
    },
    openAssign() {
      this.assignForm = { vehicleId: null, driverId: null };
      this.assignError = '';
      this.showAssign = true;
    },
    async saveAssign() {
      if (!this.assignForm.vehicleId || !this.assignForm.driverId) {
        this.assignError = 'Both vehicle and driver are required.';
        return;
      }
      this.saving = true;
      try {
        await fleetApi.assignDriverToVehicle(this.assignForm.vehicleId, this.assignForm.driverId);
        this.$toast?.success?.('Driver assigned to vehicle');
        this.showAssign = false;
        await this.loadData();
      } catch (err) {
        this.assignError = err?.message || 'Failed to assign. May already be assigned.';
      } finally {
        this.saving = false;
      }
    },
    async doUnassign(a) {
      const result = await Swal.fire({
        title: 'Remove this assignment?',
        text: 'This action cannot be undone!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, remove it!',
        cancelButtonText: 'Cancel',
      });
      if (!result.isConfirmed) return;
      try {
        await fleetApi.unassignDriverFromVehicle(a.vehicleId, a.driverId);
        this.$toast?.success?.('Assignment removed');
        await this.loadData();
      } catch (err) {
        this.$toast?.error?.(err?.message || 'Failed to unassign');
      }
    },
  },
};
</script>

<style scoped>
@import './fleet.shared.css';
</style>
